import { useState, useMemo, useEffect, useCallback } from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import { get } from 'lodash'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { useTeam } from '../../../contexts/TeamContext'
import { useProject } from '../../../contexts/ProjectContext'
import { useService } from '../../../contexts/ServiceContext'
import { useConfirmationDialog } from '../../../system/ConfirmationDialogProvider'
import { useProjects } from '../../../contexts/ProjectsContext'
import { useNodeTasks } from '../../../contexts/NodeTasksContext'
import { useDomains } from '../../../contexts/DomainsContext'
import useTeamIngressControllersResources from '../../../shared/resources/api/v1/admin/docker/useTeamIngressControllersResources'
import LoadingState from '../../../shared/components/LoadingState'
import ErrorState from '../../../shared/components/ErrorState'
import PageHeader from '../../../shared/components/layouts/PageHeader'
import PageBody from '../../../shared/components/layouts/PageBody'
import TabPanel from '../../../shared/components/TabPanel'
import ResponsiveTabs from '../../../shared/components/ResponsiveTabs'
import ServiceGeneralTab from './components/ServiceGeneralTab'
import ServiceConfigurationTab from './components/ServiceConfigurationTab'
import ServiceStorageTab from './components/ServiceStorageTab'
import ServiceNetworkingTab from './components/ServiceNetworkingTab'
import ServiceCronJobsTab from './components/ServiceCronJobsTab'
import ServiceLogsTab from './components/ServiceLogsTab'
import Flex from '../../../shared/components/layouts/Flex'
import ServiceStatusIndicator from '../../../shared/components/ServiceStatusIndicator'

function ServiceShowPage() {
  const { team } = useTeam()
  const { project } = useProject()
  const {
    // Service
    service,
    loading,
    error,
    updateService,
    destroyService,

    // Variables
    variables,
    variablesLoading,
    createVariable,
    updateVariable,
    destroyVariable,

    // Volumes
    volumes,
    volumesLoading,
    createVolume,
    updateVolume,
    destroyVolume,

    // Port Mappings
    portMappings,
    portMappingsLoading,
    createPortMapping,
    updatePortMapping,
    destroyPortMapping,

    // Dependencies
    dependencies,
    dependenciesLoading,
    availableServices,
    createDependency,
    destroyDependency,

    // Labels
    labels,
    labelsLoading,
    createLabel,
    updateLabel,
    destroyLabel,

    // Ingresses
    ingresses,
    ingressesLoading,
    createIngress,
    updateIngress,
    destroyIngress,

    // Networks
    serviceNetworks,
    serviceNetworksLoading,
    availableNetworks,
    createServiceNetwork,
    destroyServiceNetwork,

    // Cron Jobs
    cronJobs,
    cronJobsLoading,
    createCronJob,
    updateCronJob,
    destroyCronJob,
  } = useService()

  // URL params
  const { teamSlug } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const { openConfirmationDialog } = useConfirmationDialog()

  // Team-level contexts
  const { projectsById, loading: projectsLoading } = useProjects()
  const { domains, loading: domainsLoading } = useDomains()
  const { activeTaskByServiceId } = useNodeTasks()

  // Team-level resource hooks (not centralized)
  const { all: getAllIngressControllers } = useTeamIngressControllersResources(teamSlug)

  // Team-level data state for ingress controllers (not in context)
  const [teamIngressControllers, setTeamIngressControllers] = useState([])
  const [ingressControllersLoading, setIngressControllersLoading] = useState(true)

  // Load ingress controllers (not in context)
  useEffect(() => {
    setIngressControllersLoading(true)
    getAllIngressControllers()
      .then((icResponse) => {
        setTeamIngressControllers(icResponse.ingressControllers || [])
      })
      .catch(() => {
        setTeamIngressControllers([])
      })
      .finally(() => {
        setIngressControllersLoading(false)
      })
  }, [teamSlug])

  const teamDataLoading = projectsLoading || domainsLoading || ingressControllersLoading

  const handleDelete = useCallback(() => {
    openConfirmationDialog({
      title: <FormattedMessage id="dockerServices.delete.title" />,
      message: <FormattedMessage id="dockerServices.delete.message" />,
      onConfirm: () => destroyService(service.id)
        .then(() => {
          navigate(`/teams/${teamSlug}/docker/projects/${project.slug}/services`)
        })
        .catch(() => {
          enqueueSnackbar(formatMessage({ id: 'dockerServices.delete.error' }), { variant: 'error' })
        }),
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, destroyService, service, navigate, teamSlug, project, enqueueSnackbar, formatMessage])

  // Tab value from URL
  const getTabValue = () => {
    if (location.pathname.endsWith('/configuration')) return 1
    if (location.pathname.endsWith('/storage')) return 2
    if (location.pathname.endsWith('/networking')) return 3
    if (location.pathname.endsWith('/cron-jobs')) return 4
    if (location.pathname.endsWith('/logs')) return 5
    return 0
  }
  const tabValue = getTabValue()

  if (loading) return <LoadingState />
  if (error) return <ErrorState messageId="dockerServices.error" />

  const basePath = `/teams/${teamSlug}/docker/projects/${project.slug}/services/${service.slug}`
  const pendingTask = activeTaskByServiceId[service.id]

  const tabs = useMemo(() => [
    { label: <FormattedMessage id="dockerServices.tabs.general" />, to: basePath },
    { label: <FormattedMessage id="dockerServices.tabs.configuration" />, to: `${basePath}/configuration` },
    { label: <FormattedMessage id="dockerServices.tabs.storage" />, to: `${basePath}/storage` },
    { label: <FormattedMessage id="dockerServices.tabs.networking" />, to: `${basePath}/networking` },
    { label: <FormattedMessage id="dockerServices.tabs.cronJobs" />, to: `${basePath}/cron-jobs` },
    { label: <FormattedMessage id="dockerServices.tabs.logs" />, to: `${basePath}/logs` },
  ], [basePath])

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PageHeader
        title={service.name}
        subtitle={service.image}
        status={<ServiceStatusIndicator service={service} pendingTask={pendingTask} />}
        entities={{ team, project, service }}
        actions={
          <Flex gap={1}>
            <Button
              onClick={handleDelete}
              variant="outlined"
              color="error"
              size="small"
            >
              <FormattedMessage id="dockerServices.delete.button" />
            </Button>
          </Flex>
        }
      />

      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        overflow: 'hidden',
        minHeight: 0
      }}>
        <ResponsiveTabs tabs={tabs} value={tabValue} />

        <PageBody disableContainer>
          <TabPanel value={tabValue} index={0}>
            <ServiceGeneralTab
              service={service}
              updateService={updateService}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <ServiceConfigurationTab
              labels={labels}
              labelsLoading={labelsLoading}
              createLabel={createLabel}
              updateLabel={updateLabel}
              destroyLabel={destroyLabel}
              variables={variables}
              variablesLoading={variablesLoading}
              createVariable={createVariable}
              updateVariable={updateVariable}
              destroyVariable={destroyVariable}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <ServiceStorageTab
              volumes={volumes}
              volumesLoading={volumesLoading}
              createVolume={createVolume}
              updateVolume={updateVolume}
              destroyVolume={destroyVolume}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <ServiceNetworkingTab
              dependencies={dependencies}
              dependenciesLoading={dependenciesLoading}
              availableServices={availableServices}
              createDependency={createDependency}
              destroyDependency={destroyDependency}
              portMappings={portMappings}
              portMappingsLoading={portMappingsLoading}
              createPortMapping={createPortMapping}
              updatePortMapping={updatePortMapping}
              destroyPortMapping={destroyPortMapping}
              ingresses={ingresses}
              ingressesLoading={ingressesLoading}
              ingressControllers={teamIngressControllers}
              domains={domains}
              projectsById={projectsById}
              projectNodeIds={get(project, 'nodeIds', [])}
              createIngress={createIngress}
              updateIngress={updateIngress}
              destroyIngress={destroyIngress}
              teamDataLoading={teamDataLoading}
              serviceNetworks={serviceNetworks}
              serviceNetworksLoading={serviceNetworksLoading}
              availableNetworks={availableNetworks}
              createServiceNetwork={createServiceNetwork}
              destroyServiceNetwork={destroyServiceNetwork}
              currentProjectId={project.id}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={4}>
            <ServiceCronJobsTab
              cronJobs={cronJobs}
              cronJobsLoading={cronJobsLoading}
              createCronJob={createCronJob}
              updateCronJob={updateCronJob}
              destroyCronJob={destroyCronJob}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={5}>
            <ServiceLogsTab active={tabValue === 5} />
          </TabPanel>
        </PageBody>
      </Box>
    </Box>
  )
}

export default ServiceShowPage
